import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "../lib/apiClient";
import { useToast } from "../hooks/useToast";
import AdminLayout from "./admin/AdminLayout";
import { formatDateTime } from "../lib/format";

// Backend contract — keep field names in sync with handlers.SharingRow.
interface SharingRow {
  userId: string;
  email: string;
  plan: string;
  distinctDevices: number;
  distinctIps: number;
  totalRequests: number;
  firstSeen: string | null;
  lastSeen: string | null;
}

interface Summary {
  flaggedUsers: number;
  threshold: number;
  windowDays: number;
  note: string;
}

// Delegates to lib/format.ts (#1033).
function formatTime(iso: string | null): string {
  return formatDateTime(iso);
}

// DETECT-ONLY account-sharing report. Surfaces users whose distinct device
// count over the trailing 14 days meets/exceeds an (adjustable) threshold —
// a heuristic for credential sharing, NOT proof (device_id is client-supplied
// and spoofable). Nothing here blocks anyone; it's a triage list for the
// founder. Reads GET /admin/sharing-report (migration 030 view).
const AdminSharingReport: React.FC = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState<SharingRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [minDevices, setMinDevices] = useState(8);

  const fetchReport = useCallback(
    async (threshold: number) => {
      setLoading(true);
      try {
        const { data } = await apiClient.get<{ data: SharingRow[]; summary: Summary }>(
          `/admin/sharing-report?min_devices=${threshold}`
        );
        setRows(data.data || []);
        setSummary(data.summary || null);
      } catch (err: any) {
        if (err?.response?.status === 403) {
          toast("Admin access required", "error");
        } else {
          toast("Could not load sharing report", "error");
        }
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  // Initial load at the default threshold; subsequent threshold changes are
  // applied via the Apply button (fetchReport is stable — runs once).
  useEffect(() => {
    fetchReport(8);
  }, [fetchReport]);

  return (
    <AdminLayout pageTitle="Account Sharing (last 14 days)">
      <div className="admin-feedback">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          <label style={{ fontSize: 13, color: "var(--text-secondary)" }}>
            Flag users with at least{" "}
            <input
              type="number"
              min={1}
              value={minDevices}
              onChange={(e) => setMinDevices(Math.max(1, Number(e.target.value) || 1))}
              style={{ width: 56 }}
            />{" "}
            distinct devices
          </label>
          <button className="modal-share-btn" onClick={() => fetchReport(minDevices)}>
            Apply
          </button>
        </div>

        {summary && (
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
            {summary.flaggedUsers} user{summary.flaggedUsers === 1 ? "" : "s"} flagged at
            {" "}≥{summary.threshold} devices over {summary.windowDays} days. {summary.note}
          </div>
        )}

        {loading && rows.length === 0 ? (
          <div className="admin-feedback-empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="admin-feedback-empty">
            No accounts exceed the device threshold in the last 14 days — no sharing
            signal yet.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="scraper-health-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Plan</th>
                  <th style={{ textAlign: "right" }}>Devices</th>
                  <th style={{ textAlign: "right" }}>IPs</th>
                  <th style={{ textAlign: "right" }}>Requests</th>
                  <th>First seen</th>
                  <th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.userId} data-sev={r.distinctDevices >= 12 ? "critical" : "warn"}>
                    <td>{r.email}</td>
                    <td>{r.plan}</td>
                    <td style={{ textAlign: "right" }}>{r.distinctDevices}</td>
                    <td style={{ textAlign: "right" }}>{r.distinctIps}</td>
                    <td style={{ textAlign: "right" }}>{r.totalRequests}</td>
                    <td>{formatTime(r.firstSeen)}</td>
                    <td>{formatTime(r.lastSeen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSharingReport;
